import React, { ReactElement, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FlattenSimpleInterpolation } from "styled-components";

import { Link } from "react-router-dom";
import { formatNumber, getWineBaseColor, checkIfWineTypeIsOrangeWine } from "../../../helpers/tools";
import { I18N_NAMESPACES, WINE_CLASS_IDS, WINE_LIST_SLUGS } from "../../../helpers/constants";
import URL_PATTERNS from "../../../urls";

import AddToBoxOrCartButton from "../../molecules/AddToBoxOrCartButton";
import { InfoBoxItemProductHolderButtonCartPrice } from "../../atoms/SpecialInfoBoxItem";
import arrowBack from "../../../assets/images/icons/arrow-left-straight.svg";

import defaultBottleImage from "../../../assets/images/temp/default-bottle.png";
import BookmarkIcon from "../../atoms/BookmarkIcon";
import { GetWine_wine } from "../../../types/__generated__/GetWine";
import { Credit } from "../../../types/__generated__/Credit";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { MemberRewardBasicInfo } from "../../../types/__generated__/MemberRewardBasicInfo";
import ShareAndBookmark from "../../atoms/ShareAndBookmark";
import { colors } from "../../../styles/variables";
import ProductDetailsTags from "../../atoms/ProductDetailsTags";
import { useDeviceSize } from "../../../helpers/hooks";
import { useTopInsetBg } from "../Layout/context";
import TastebudProfileListPlotV2 from "../../molecules/TastebudProfileListPlotV2";
import TasteSummary from "../../atoms/TasteSummary";
import WineRelatedBadges from "../../molecules/WineRelatedBadges";
import GuidesRelatedToWineSection from "../../molecules/GuidesRelatedToWineSection";
import WinePrimaryFlavours from "../../molecules/WinePrimaryFlavours";
import WineStory from "../../molecules/WineStory";
import WineRegion from "../../molecules/WineRegion";
import WineSpecs from "../../atoms/WineSpecs";
import WineRelatedWines from "../../molecules/WineRelatedWines";

import { isLoggedIn } from "../../../helpers/auth";
import PairingGuide from "../../molecules/PairingGuide";
import { getCashAmountLabel } from "../../../helpers/i18n";
import {
  CloseTrayButton,
  InfoBoxItemDescriptionWrapper,
  Title,
  VerticalLine,
  MatchAndRatingInfoWrapper,
  MatchAndRatingInfoLabel,
  StyledAddWineToShoppingCartButton,
  StyledInfoBoxItem,
  StyledInfoBoxItemDescriptionHolder,
  StyledInfoBoxItemProductHolder,
  StyledInfoBoxItemProductHolderButtons,
  StarIcon,
  PenIcon,
  PronunciationText,
  IconContainer,
  HeroImageTopRightSection,
  ProductHero,
  ArrowsExpandableIcon,
  InfoBoxItemProductImage,
  ProductDetailsTagsWrapper,
  ReviewLabelWrapper,
  Row,
  Column,
  TasteProfileWrapper,
} from "./style";

interface MainBottleInfoProps {
  wine: GetWine_wine | null | undefined;
  hasWineBeenReview: boolean;
  memberShoppingCartQuery: MemberShoppingCart | MemberRewardBasicInfo | null | undefined;
  handleOffCanvasChange: Function;
  onReviewWineClick: Function;
  handleToggleBottleInfoTray?: Function;
  infoBoxItemStyle?: FlattenSimpleInterpolation;
  addWineToShoppingCartButtonStyle?: FlattenSimpleInterpolation;
  infoBoxItemDescriptionHolderStyle?: FlattenSimpleInterpolation;
  infoBoxItemProductHolderButtonsStyle?: FlattenSimpleInterpolation;
  infoBoxItemProductHolderStyle?: FlattenSimpleInterpolation;
  infoBoxItemDescriptionWrapperStyle?: FlattenSimpleInterpolation;
  forceAddToBox?: boolean;
  isAddingAnother?: boolean;
  isInBoxAndCartTray?: boolean;
  setHeaderDisplay?: Function;
  isInSideTray?: boolean;
  isWineDetailsPage?: boolean;
  memberId?: number;
  addBottleToCredit?: Credit;
  locationId?: number;
}

const MainBottleInfo = ({
  wine,
  onReviewWineClick,
  hasWineBeenReview,
  handleOffCanvasChange,
  handleToggleBottleInfoTray = () => {},
  infoBoxItemStyle = [],
  addWineToShoppingCartButtonStyle = [],
  infoBoxItemProductHolderButtonsStyle = [],
  infoBoxItemDescriptionHolderStyle = [],
  infoBoxItemProductHolderStyle = [],
  infoBoxItemDescriptionWrapperStyle = [],
  memberShoppingCartQuery,
  memberId,
  forceAddToBox = false,
  isAddingAnother = false,
  isInBoxAndCartTray = false,
  setHeaderDisplay = () => {},
  isInSideTray = false,
  isWineDetailsPage,
  addBottleToCredit,
  locationId,
}: MainBottleInfoProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.WINE_PAGES, I18N_NAMESPACES.COMMON]);
  const [isAddWineTrayOpen, setIsAddWineTrayOpen] = useState<boolean>(false);
  const [isWineImagePressed, setIsWineImagePressed] = useState<boolean>(false);
  const [hasPassedAddBottleButtonOnMobile, setHasPassedAddBottleButtonOnMobile] = useState<boolean>(false);
  const rowOnMobileRef = useRef<HTMLDivElement | null>(null);
  const tasteProfileRef = useRef<HTMLDivElement | null>(null);

  const { isSmall } = useDeviceSize();
  const shouldShowFixedAddBottleButton = isSmall && hasPassedAddBottleButtonOnMobile;
  const shouldShowVerticalLineOnAddBottleButton = isSmall && !hasPassedAddBottleButtonOnMobile;
  const shouldShowArrowsExpandableIcon = isInSideTray && !isSmall;

  /**
   * For non subscribers and non-logged in customers,
   * when they click to add wine, <WinesAddBottleTray> is shown,
   * however, Wine Details back button is stacked on top.
   * This function changes the state to control the visibility of back button in Wine Details tray.
   * */
  const toggleAddWineTray = () => {
    setIsAddWineTrayOpen(!isAddWineTrayOpen);
  };

  /**
   * Gets the wine details tags from GET_WINE_NAME_AND_LARGE_PHOTO query,
   * and returns the Wine Class name, Grape Type, Wine Region, and Country in an array.
   * */
  const getWineDetailsTagNames = () => {
    const tags = {
      wineClassName: wine?.wineType?.wineClass.name,
      wineTypeName: wine?.wineType?.name,
      wineRegionName: wine?.wineRegion?.name,
      wineRegionState: wine?.wineRegion?.state,
      wineCountryName: wine?.country?.name,
      wineProductionMethods: wine?.wineProductionMethods,
      wineMakeName: wine?.wineMaker?.name,
    };
    return tags;
  };

  const handleScroll = () => {
    if (!rowOnMobileRef) {
      return;
    }
    const hasPassedAddBottleButton = (rowOnMobileRef?.current?.getBoundingClientRect().bottom ?? 0) < 0;
    setHasPassedAddBottleButtonOnMobile(!!hasPassedAddBottleButton);
  };

  useEffect(() => {
    window.addEventListener("scroll", () => handleScroll());
    return () => {
      window.removeEventListener("scroll", () => handleScroll());
    };
  }, []);

  const memberLikelihood = formatNumber(100 * (wine?.memberLikelihood ?? 0), 0, false);

  const { wineType, product } = wine || {};
  const { slug } = product || {};
  const { id: wineTypeId, wineClass } = wineType || {};
  const { id: wineClassId } = wineClass || {};
  const baseColor = wine && getWineBaseColor(wineClassId ?? 0, wineTypeId ?? 0);

  /* Notch zone matches the wine's class colour while this hero is mounted. */
  useTopInsetBg(baseColor || null);

  const addButtonLabel = wine?.hasStock ? t("common:addBottle") : t("common:noStock");

  const photoUrl = wine?.product?.productPhotos?.length
    ? wine?.product?.productPhotos[0]?.photoLarge
    : defaultBottleImage;

  const { isBookmarked } = wine || {};
  const isSake = wine?.wineType?.wineClass?.id === WINE_CLASS_IDS.DB_ID_WINE_CLASS_SAKE;
  const isOrangeWine = checkIfWineTypeIsOrangeWine(wine?.wineType?.id ?? 0);

  const shouldRenderMatchLabel = !!(
    wine?.memberWineRatingScore === null &&
    wine?.memberLikelihood &&
    !isSake
  );

  const pronunciation = wine?.wineType?.pronunciation;
  const pronunciationCopy = t("winePages:pronunciation", {
    pronunciation,
    context: isSake ? "sake" : "wine",
    interpolation: { escapeValue: false },
  });

  const handlematchLabelClick = () => {
    tasteProfileRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const matchLabel = shouldRenderMatchLabel && (
    <MatchAndRatingInfoWrapper onClick={() => handlematchLabelClick()}>
      <MatchAndRatingInfoLabel>
        {t("winePages:percentMatch", { percent: memberLikelihood })}
      </MatchAndRatingInfoLabel>
    </MatchAndRatingInfoWrapper>
  );

  const ratingLabel = wine?.memberWineRatingScore && (
    <MatchAndRatingInfoWrapper onClick={() => onReviewWineClick()} isRatingLabel>
      <MatchAndRatingInfoLabel isRating>{t("common:yourRating")}</MatchAndRatingInfoLabel>
      <StarIcon />
      {`${wine?.memberWineRatingScore}/5`}
    </MatchAndRatingInfoWrapper>
  );

  const reviewLabel = wine?.memberWineRatingScore && (
    <ReviewLabelWrapper onClick={() => onReviewWineClick()}>
      <PenIcon />
      {hasWineBeenReview ? t("winePages:seeMyReview") : t("winePages:addMyReview")}
    </ReviewLabelWrapper>
  );

  const bookMarkIcon = (
    <IconContainer onClick={() => handleOffCanvasChange()} isAddWineTrayOpen={isAddWineTrayOpen}>
      <BookmarkIcon isBookmarked={isBookmarked} />
    </IconContainer>
  );

  const arrowsExpandableIcon = (
    <Link to={URL_PATTERNS.WINE_DETAILS(slug)} target="_blank">
      <IconContainer isAddWineTrayOpen={isAddWineTrayOpen}>
        <ArrowsExpandableIcon />
      </IconContainer>
    </Link>
  );

  const renderAddToBoxOrCartButton = (): ReactElement => (
    <StyledInfoBoxItemProductHolderButtons
      css={infoBoxItemProductHolderButtonsStyle}
      shouldShowFixedAddBottleButton={shouldShowFixedAddBottleButton}
      isInBoxAndCartTray={isInBoxAndCartTray}
    >
      <AddToBoxOrCartButton
        forceAddToBox={forceAddToBox}
        isAddingAnother={isAddingAnother}
        isInBoxAndCartTray={isInBoxAndCartTray}
        css={addWineToShoppingCartButtonStyle}
        disabled={!wine?.hasStock}
        memberShoppingCartQuery={memberShoppingCartQuery}
        wine={wine}
        ButtonComponent={StyledAddWineToShoppingCartButton}
        label=""
        submittingLabel=""
        extraCallback={handleToggleBottleInfoTray}
        toggleAddWineTray={() => {
          toggleAddWineTray();
          setHeaderDisplay && setHeaderDisplay();
        }}
        addBottleToCredit={addBottleToCredit}
      >
        {addButtonLabel}
        {shouldShowVerticalLineOnAddBottleButton && <VerticalLine />}
        <InfoBoxItemProductHolderButtonCartPrice>
          {getCashAmountLabel(wine?.product?.sellingPrice)}
        </InfoBoxItemProductHolderButtonCartPrice>
      </AddToBoxOrCartButton>
    </StyledInfoBoxItemProductHolderButtons>
  );

  const renderProductDetailsTags = (): ReactElement => (
    <ProductDetailsTagsWrapper isWineDetailsPage={!!isWineDetailsPage}>
      <ProductDetailsTags
        tags={getWineDetailsTagNames()}
        color={colors.transparent}
        handleToggleBottleInfoTray={() => handleToggleBottleInfoTray && handleToggleBottleInfoTray()}
        pathname={URL_PATTERNS.WINE_LIST(WINE_LIST_SLUGS.ALL_WINES)}
        isInDetailsTrayOrPage
      />
    </ProductDetailsTagsWrapper>
  );

  const renderHeroImage = (): ReactElement => (
    <StyledInfoBoxItemProductHolder css={infoBoxItemProductHolderStyle}>
      <ProductHero $bg={baseColor ?? colors.blue} isInSideTray={!!isInSideTray}>
        {isInSideTray && (
          <CloseTrayButton
            onClick={() => handleToggleBottleInfoTray && handleToggleBottleInfoTray()}
            isAddWineTrayOpen={!!isAddWineTrayOpen}
          >
            <img src={arrowBack} alt="Close button" />
          </CloseTrayButton>
        )}
        <HeroImageTopRightSection>
          {matchLabel}
          {ratingLabel}
          {reviewLabel}
          {bookMarkIcon}
          {shouldShowArrowsExpandableIcon && arrowsExpandableIcon}
        </HeroImageTopRightSection>
        <InfoBoxItemProductImage
          src={photoUrl ?? ""}
          alt="productImage"
          isInSideTray={!!isInSideTray}
          onClick={() => {
            setIsWineImagePressed(!isWineImagePressed);
          }}
          isPressed={isWineImagePressed}
        />
        {renderProductDetailsTags()}
      </ProductHero>
      {!isSmall && !locationId && renderAddToBoxOrCartButton()}
    </StyledInfoBoxItemProductHolder>
  );

  const renderBottleInfoTopPart = (): ReactElement => (
    <>
      <Title>{wine?.product?.name ?? ""}</Title>
      {!isSmall && (
        <Row>
          {!!pronunciation && <PronunciationText>{pronunciationCopy}</PronunciationText>}
          <ShareAndBookmark
            isInBoxAndCartTray={isInBoxAndCartTray}
            wine={wine}
            handleWineListsPanelChange={() => handleOffCanvasChange()}
            isWineDetailsPage={!!isWineDetailsPage}
          />
        </Row>
      )}
      {isSmall && (
        <Row ref={rowOnMobileRef}>
          {!locationId && renderAddToBoxOrCartButton()}
          <ShareAndBookmark
            isInBoxAndCartTray={isInBoxAndCartTray}
            wine={wine}
            handleWineListsPanelChange={() => handleOffCanvasChange()}
          />
        </Row>
      )}
    </>
  );

  const renderBottleInfoPart = (): ReactElement => (
    <InfoBoxItemDescriptionWrapper css={infoBoxItemDescriptionWrapperStyle}>
      <StyledInfoBoxItemDescriptionHolder
        css={infoBoxItemDescriptionHolderStyle}
        withExtraBottomSpacing={shouldShowFixedAddBottleButton}
      >
        {renderBottleInfoTopPart()}
        <WinePrimaryFlavours wine={wine} />
        <WineStory wine={wine} />
      </StyledInfoBoxItemDescriptionHolder>
      {shouldShowFixedAddBottleButton && !locationId && renderAddToBoxOrCartButton()}
    </InfoBoxItemDescriptionWrapper>
  );

  const renderTasteProfile = (): ReactElement => (
    <TasteProfileWrapper ref={tasteProfileRef} isWineDetailsPage={!!isWineDetailsPage}>
      <TastebudProfileListPlotV2
        wine={wine}
        isWineDetailsPage={!!isWineDetailsPage}
        isOrangeWine={isOrangeWine}
      />
    </TasteProfileWrapper>
  );

  const renderTasteSummary = (): ReactElement => (
    <TasteSummary wineSlug={wine?.product?.slug} isWineDetailsPage={!!isWineDetailsPage} />
  );

  const renderWineRegion = (): ReactElement => (
    <WineRegion wine={wine} isWineDetailsPage={!!isWineDetailsPage} />
  );

  const renderSpecs = (): ReactElement => (
    <WineSpecs wine={wine} isWineDetailsPage={!!isWineDetailsPage} isSake={isSake} />
  );

  const renderPairingGuide = (): ReactElement => (
    <PairingGuide wine={wine} isWineDetailsPage={!!isWineDetailsPage} />
  );

  const renderWineRelatedBadges = (): ReactElement | null => {
    if (!isLoggedIn() || !wine) {
      return null;
    }

    return (
      <WineRelatedBadges
        wine={wine}
        memberShoppingCartQuery={memberShoppingCartQuery}
        isInBoxAndCartTray={isInBoxAndCartTray}
        isWineDetailsPage={!!isWineDetailsPage}
      />
    );
  };

  const renderGuidesRelatedToWine = (): ReactElement => (
    <GuidesRelatedToWineSection wine={wine} isWineDetailsPage={!!isWineDetailsPage} />
  );

  const renderRelatedWines = (): ReactElement => (
    <WineRelatedWines
      wine={wine}
      isInBoxAndCartTray={isInBoxAndCartTray}
      memberShoppingCartQuery={memberShoppingCartQuery}
      memberId={memberId}
      handleToggleBottleInfoTray={handleToggleBottleInfoTray}
      isWineDetailsPage={!!isWineDetailsPage}
      locationId={locationId}
    />
  );

  return (
    <StyledInfoBoxItem css={infoBoxItemStyle} onScroll={handleScroll}>
      {renderHeroImage()}
      <Column isInSideTray={isInSideTray}>
        {renderBottleInfoPart()}
        {!isSake && renderTasteProfile()}
        {!isSake && renderTasteSummary()}
        {renderSpecs()}
        {renderPairingGuide()}
        {renderRelatedWines()}
        {renderWineRegion()}
        {renderGuidesRelatedToWine()}
        {renderWineRelatedBadges()}
      </Column>
    </StyledInfoBoxItem>
  );
};

export default MainBottleInfo;
